import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  DateTimeField,
  MutationError,
  ProblemError,
  hasGrant,
  usePrincipal,
} from "@csa/web-foundation";
import { useT } from "../../i18n/index";
import type { MessageKey } from "../../i18n/dictionaries";
import { Icon } from "../../ui/icons";
import { StatusPill } from "./issuance-home";
import {
  useApplication,
  useConfirmIssuance,
  useObservePayment,
  useRecordDeclined,
  useResolveException,
  useRevalidateQuote,
  useSubmit,
  useWithdrawApplication,
} from "./use-issuance";
import type { ExceptionType, PolicyApplication } from "./types";

// B5 — the issuance-desk detail. Drives one application create → submit → observe-payment
// → confirm-issuance with the price-integrity gate visible, and renders the exception
// panel (status = ISSUANCE_EXCEPTION) with its exception_type as a DISTINCT field.
// CSA holds no money (observe-and-record); off-price is four-eyes; RBAC gates the
// affordances (the API enforces). No price compute/override in the browser.

export function IssuanceDetail() {
  const { t } = useT();
  const { id } = useParams();
  const { me } = usePrincipal();
  const q = useApplication(id);

  // QA round — hasGrant treats the scope as a FLOOR, so "own" also passes for a
  // principal granted only at own/team, while every write route here is
  // @RequirePermission("application:write","all"). That advisor would see live
  // controls and collect a 403. The seed only ever grants these at `all`
  // (roles.ts:142), so no such principal exists today — but the client check should
  // state the same requirement as the API rather than rely on that.
  const canWrite = hasGrant(me, "application:write", "all");
  const canIssue = hasGrant(me, "application:issue", "all");

  if (q.isLoading)
    return (
      <div className="k-loading">
        <div className="k-loading__head">
          <span className="k-loading__spinner" />
          <span className="k-loading__label">{t("iss.loading")}</span>
        </div>
      </div>
    );
  if (q.error || !q.data) {
    return (
      <div className="k-note k-note--error">
        <div className="k-note__row">
          <span className="k-note__icon k-note__icon--error">
            <Icon name="alert-triangle" size={20} />
          </span>
          <p role="alert" className="k-note__desc" data-testid="detail-error">
            {t("iss.error")}
          </p>
        </div>
      </div>
    );
  }

  const app = q.data;

  return (
    <section className="surface issuance-detail" data-testid="issuance-detail">
      <header className="surface__head issuance-detail-head">
        <div>
          <h1 className="surface__title">{t("iss.detailHeading")}</h1>
        </div>
        <StatusPill status={app.status} exceptionType={app.exceptionType} t={t} />
      </header>

      {/* The snapshotted accepted price — the price-integrity anchor + context summary. */}
      <dl className="issuance-summary k-card k-card--pad-sm">
        <div>
          <dt className="surface__section-label" style={{ margin: "0 0 4px" }}>
            {t("iss.acceptedPrice")}
          </dt>
          <dd data-testid="accepted-price" style={{ margin: 0, color: "var(--k-ink)" }}>
            {app.acceptedPremium} {app.currency}
          </dd>
        </div>
        <div>
          <dt className="surface__section-label" style={{ margin: "0 0 4px" }}>
            {t("iss.line")}
          </dt>
          <dd style={{ margin: 0, color: "var(--k-ink)" }}>
            {t(`iss.line.${app.lineCode}` as MessageKey)}
          </dd>
        </div>
        <div>
          <dt className="surface__section-label" style={{ margin: "0 0 4px" }}>
            {t("iss.carrier")}
          </dt>
          <dd style={{ margin: 0, color: "var(--k-ink)" }}>{app.carrierId.slice(0, 8)}</dd>
        </div>
        {app.issuedPremium ? (
          <div>
            <dt className="surface__section-label" style={{ margin: "0 0 4px" }}>
              {t("iss.issuedPrice")}
            </dt>
            <dd data-testid="issued-price" style={{ margin: 0, color: "var(--k-ink)" }}>
              {app.issuedPremium} {app.currency}
            </dd>
          </div>
        ) : null}
      </dl>

      {/* Stage-driven action panels — gated by status AND permission. */}
      {app.status === "DRAFT" ? <SubmitStage id={app.id} canWrite={canWrite} t={t} /> : null}
      {app.status === "SUBMITTED_TO_CARRIER" ? (
        <ObservePaymentStage id={app.id} canWrite={canWrite} t={t} />
      ) : null}
      {app.status === "ISSUANCE_PENDING" ? (
        <ConfirmIssuanceStage app={app} canIssue={canIssue} t={t} />
      ) : null}
      {app.status === "ISSUED" ? <IssuedStage app={app} t={t} /> : null}
      {app.status === "ISSUANCE_EXCEPTION" ? (
        <ExceptionPanel app={app} canWrite={canWrite} t={t} />
      ) : null}
      {/* GAP-AUDIT-3 D16 — QUOTE_REVALIDATION had NO panel at all. An application
          whose quote expired on submit lands here, and with nothing rendered it was
          an invisible dead end: no way forward, no way to close it. */}
      {app.status === "QUOTE_REVALIDATION" ? (
        <RevalidationStage id={app.id} canWrite={canWrite} t={t} />
      ) : null}
      {/* D16 — the two terminal exits, available on ANY non-terminal application.
          Without them an application that will never issue stayed in the ops queue
          forever. Kept last and visually quiet: these are the "this one is over"
          actions, not the happy path. */}
      {!TERMINAL_STATUSES.includes(app.status) ? (
        <TerminateStage id={app.id} canWrite={canWrite} t={t} />
      ) : null}
    </section>
  );
}

/** Mirrors the service's TERMINAL set (policy-application.service.ts:25). A terminal
 *  application accepts neither withdraw nor record-declined — the server 409s. */
const TERMINAL_STATUSES: readonly PolicyApplication["status"][] = [
  "ISSUED",
  "FAILED",
  "WITHDRAWN",
  "DECLINED",
];

type TFn = (k: MessageKey) => string;

function SubmitStage({ id, canWrite, t }: { id: string; canWrite: boolean; t: TFn }) {
  const submit = useSubmit(id);
  return (
    <div className="issuance-stage k-card" data-testid="stage-submit">
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("iss.stage.submit")}
      </p>
      <p className="muted small">{t("iss.stage.submitHint")}</p>
      {canWrite ? (
        <button
          type="button"
          className="k-btn k-btn--primary"
          data-testid="action-submit"
          disabled={submit.isPending}
          onClick={() => submit.mutate()}
        >
          <Icon name="arrow-right" size={16} />
          {t("iss.action.submit")}
        </button>
      ) : (
        <p role="note" className="muted small" data-testid="submit-restricted">
          {t("iss.needWrite")}
        </p>
      )}
      {/* THE MONEY PATH MUST NOT FAIL SILENTLY. Every stage here fired its
          mutation and rendered no outcome, so an operator could not tell a
          rejected issuance from a completed one — on the one flow where that
          distinction becomes a policy that does or does not exist. */}
      <MutationError
        error={submit.error}
        fallback={t("iss.actionFailed")}
        offline={t("common.offline")}
      />
    </div>
  );
}

function ObservePaymentStage({ id, canWrite, t }: { id: string; canWrite: boolean; t: TFn }) {
  const observe = useObservePayment(id);
  const [reference, setReference] = useState("");
  return (
    <div className="issuance-stage k-card" data-testid="stage-observe">
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("iss.stage.observe")}
      </p>
      {/* Observe-and-record — CSA holds no money; the payment goes to the carrier. */}
      <p className="muted small" data-testid="observe-hint">
        {t("iss.stage.observeHint")}
      </p>
      {canWrite ? (
        <div className="issuance-form">
          <label className="k-field">
            <span className="k-field__label">{t("iss.paymentReference")}</span>
            <input
              className="k-input ui-input"
              data-testid="payment-reference"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="k-btn k-btn--primary"
            data-testid="action-observe"
            disabled={observe.isPending || reference.trim().length === 0}
            onClick={() => observe.mutate({ paymentReference: reference.trim() })}
          >
            <Icon name="check" size={16} />
            {t("iss.action.observe")}
          </button>
        </div>
      ) : (
        <p role="note" className="muted small">
          {t("iss.needWrite")}
        </p>
      )}
      <MutationError
        error={observe.error}
        fallback={t("iss.actionFailed")}
        offline={t("common.offline")}
      />
    </div>
  );
}

function ConfirmIssuanceStage({
  app,
  canIssue,
  t,
}: {
  app: PolicyApplication;
  canIssue: boolean;
  t: TFn;
}) {
  const confirm = useConfirmIssuance(app.id);
  const [carrierPolicyNumber, setCarrierPolicyNumber] = useState("");
  const [proofRef, setProofRef] = useState("");
  // Default the issued premium to the accepted one — a clean match is the norm; a change
  // is what surfaces the gate. The BOUNDARY decides pass/fail; the browser computes nothing.
  const [issuedPremium, setIssuedPremium] = useState(app.acceptedPremium);

  return (
    <div className="issuance-stage k-card" data-testid="stage-confirm">
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("iss.stage.confirm")}
      </p>
      {/* The price-integrity gate, made visible (not computed here) — a calm governed rule. */}
      <div
        className="issuance-gate k-note k-card--accent-amber"
        data-testid="price-integrity-gate"
        style={{ marginBottom: 16 }}
      >
        <div className="k-note__row">
          <span className="k-note__icon k-note__icon--restricted">
            <Icon name="lock" size={18} />
          </span>
          <p className="k-note__desc">
            {t("iss.gate.rule")} — {t("iss.acceptedPrice")}: {app.acceptedPremium} {app.currency}
          </p>
        </div>
      </div>
      {canIssue ? (
        <div className="issuance-form">
          <label className="k-field">
            <span className="k-field__label">{t("iss.carrierPolicyNumber")}</span>
            <input
              className="k-input ui-input"
              data-testid="carrier-policy-number"
              value={carrierPolicyNumber}
              onChange={(e) => setCarrierPolicyNumber(e.target.value)}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("iss.issuedPremium")}</span>
            <input
              className="k-input ui-input"
              data-testid="issued-premium"
              value={issuedPremium}
              onChange={(e) => setIssuedPremium(e.target.value)}
            />
          </label>
          <label className="k-field">
            <span className="k-field__label">{t("iss.proofRef")}</span>
            <input
              className="k-input ui-input"
              data-testid="proof-ref"
              value={proofRef}
              onChange={(e) => setProofRef(e.target.value)}
            />
          </label>
          {/* Proof-document ATTACH depends on N6 Documents — until then, proof is a
              reference/id, not an uploaded file. */}
          <p className="muted small" data-testid="proof-n6-flag">
            {t("iss.proofN6Flag")}
          </p>
          <button
            type="button"
            className="k-btn k-btn--primary"
            data-testid="action-confirm"
            disabled={
              confirm.isPending ||
              carrierPolicyNumber.trim().length === 0 ||
              issuedPremium.trim().length === 0
            }
            onClick={() =>
              confirm.mutate({
                carrierPolicyNumber: carrierPolicyNumber.trim(),
                issuedPremium: issuedPremium.trim(),
                proofDocumentRef: proofRef.trim() || undefined,
              })
            }
          >
            <Icon name="check-circle" size={16} />
            {t("iss.action.confirm")}
          </button>
          <MutationError
            error={confirm.error}
            fallback={t("iss.actionFailed")}
            offline={t("common.offline")}
          />
        </div>
      ) : (
        <p role="note" className="muted small" data-testid="confirm-restricted">
          {t("iss.needIssue")}
        </p>
      )}
    </div>
  );
}

function IssuedStage({ app, t }: { app: PolicyApplication; t: TFn }) {
  return (
    <div className="issuance-stage issuance-stage--issued k-card" data-testid="stage-issued">
      <span className="k-badge k-badge--success">
        <Icon name="check-circle" size={14} />
        {t("iss.issuedBadge")}
      </span>
      <p data-testid="issued-body" style={{ marginTop: 10, color: "var(--k-ink-2)" }}>
        {t("iss.issuedBody")}
      </p>
      {app.resultingPolicyId ? (
        <p className="muted small">
          {t("iss.policyRef")}: {app.resultingPolicyId.slice(0, 8)}
        </p>
      ) : null}
    </div>
  );
}

// The exception panel — status is ISSUANCE_EXCEPTION (the status field); exception_type is
// rendered as its OWN field with plain-language "what happened + what next". PAID_NOT_ISSUED
// (paid the carrier, nothing issued) is the highest-priority, customer-protection case — it
// wears the danger-bordered note so it is impossible to miss.
function ExceptionPanel({
  app,
  canWrite,
  t,
}: {
  app: PolicyApplication;
  canWrite: boolean;
  t: TFn;
}) {
  const resolve = useResolveException(app.id);
  const [reason, setReason] = useState("");
  const exType: ExceptionType | null = app.exceptionType;
  const critical = exType === "PAID_NOT_ISSUED";

  return (
    <div
      className={`issuance-exception k-note${critical ? " k-note--error issuance-exception--critical" : ""}`}
      data-testid="exception-panel"
      data-critical={critical ? "true" : undefined}
    >
      <div className="k-note__row">
        <span
          className={`k-note__icon ${critical ? "k-note__icon--error" : "k-note__icon--restricted"}`}
        >
          <Icon name={critical ? "alert-triangle" : "info"} size={20} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <p className="k-note__title">{t("iss.exceptionHeading")}</p>
          {/* status and exception_type as TWO distinct fields. */}
          <div className="issuance-exception-fields">
            <p style={{ margin: "6px 0 0" }}>
              <span className="muted small">{t("iss.statusField")}:</span>{" "}
              <span data-testid="exception-status-field">{t("iss.status.ISSUANCE_EXCEPTION")}</span>
            </p>
            {exType ? (
              <p
                className={critical ? "issuance-exception-critical-line" : undefined}
                style={{ margin: "4px 0 0", fontWeight: critical ? 700 : undefined }}
              >
                <span className="muted small">{t("iss.exceptionTypeField")}:</span>{" "}
                <span data-testid="exception-type-field">
                  {t(`iss.exception.${exType}` as MessageKey)}
                </span>
              </p>
            ) : null}
          </div>
          {exType ? (
            <p className="k-note__desc" data-testid="exception-explain" style={{ marginTop: 8 }}>
              {t(`iss.exceptionExplain.${exType}` as MessageKey)}
            </p>
          ) : null}
          {exType === "PRICE_INTEGRITY_FAIL" ? (
            <p role="note" className="muted small" data-testid="offprice-foureyes-note">
              {t("iss.offPriceFourEyes")}
            </p>
          ) : null}

          {canWrite ? (
            <div className="issuance-form" data-testid="resolve-form" style={{ marginTop: 14 }}>
              <label className="k-field">
                <span className="k-field__label">{t("iss.resolveReason")}</span>
                <input
                  className="k-input"
                  data-testid="resolve-reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </label>
              <div
                className="issuance-resolve-actions"
                style={{ display: "flex", gap: 10, flexWrap: "wrap" }}
              >
                <button
                  type="button"
                  className="k-btn k-btn--secondary"
                  data-testid="action-retry"
                  disabled={resolve.isPending || reason.trim().length === 0}
                  onClick={() => resolve.mutate({ resolution: "RETRY", reason: reason.trim() })}
                >
                  {t("iss.action.retry")}
                </button>
                <button
                  type="button"
                  className="k-btn k-btn--danger"
                  data-testid="action-fail"
                  disabled={resolve.isPending || reason.trim().length === 0}
                  onClick={() => resolve.mutate({ resolution: "FAIL", reason: reason.trim() })}
                >
                  {t("iss.action.fail")}
                </button>
              </div>
              <MutationError
                error={resolve.error}
                fallback={t("iss.actionFailed")}
                offline={t("common.offline")}
              />
            </div>
          ) : (
            <p role="note" className="muted small">
              {t("iss.needWrite")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

// GAP-AUDIT-3 D16 — QUOTE_REVALIDATION recovery. `submit` moves an application here
// when the quote validity has lapsed, and POST …/revalidate-quote is the ONLY way
// out (the service rejects it from any other state). The console rendered nothing for
// this status, so a lapsed application was an invisible dead end.
//
// All three fields are REQUIRED by revalidateQuoteSchema and none are server-derived:
// unlike the create path (F2-B, which derives them from the recommendation), the new
// price comes from a carrier RE-QUOTE the server has never seen, so the advisor
// supplies it. The offer id stays a plain input on purpose: GET /v1/quotes/:id/offers
// DOES exist, but nothing here knows WHICH quote — the application row carries no
// quote reference and GET /v1/quotes takes only cursor/limit (no partyId/lineCode
// filter), so the only way to find it would be to page the caller's whole quote list
// and filter in the browser. That is the unbounded-scan pattern D13 was about. The
// picker is backlog (a filtered quotes read), not something to fake.
function RevalidationStage({ id, canWrite, t }: { id: string; canWrite: boolean; t: TFn }) {
  const revalidate = useRevalidateQuote(id);
  const [offerId, setOfferId] = useState("");
  const [acceptedAt, setAcceptedAt] = useState("");
  const [validUntil, setValidUntil] = useState("");

  // revalidateQuoteSchema types acceptedOfferId as z.string().uuid(); without this the
  // advisor pastes a wrong-shaped id and gets a bare 400 with no explanation.
  const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  const offerIdOk = UUID_RE.test(offerId.trim());
  const ready = offerIdOk && acceptedAt.trim().length > 0 && validUntil.trim().length > 0;

  return (
    <div className="issuance-stage k-card" data-testid="stage-revalidation">
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("iss.stage.revalidate")}
      </p>
      <p className="muted small" data-testid="revalidate-hint">
        {t("iss.stage.revalidateHint")}
      </p>
      {canWrite ? (
        <form
          className="issuance-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!ready || revalidate.isPending) return;
            revalidate.mutate({
              acceptedOfferId: offerId.trim(),
              // DO NOT "simplify" these away. DateTimeField emits an Istanbul
              // OFFSET form (…T09:00:00+03:00), and revalidateQuoteSchema uses a
              // plain z.string().datetime(), which rejects an offset — only `Z` is
              // accepted. (Contrast commission.ts, which opts in with
              // datetime({offset:true}) precisely because it takes the field's raw
              // output.) Without this round-trip the POST is a 400.
              acceptedAt: new Date(acceptedAt).toISOString(),
              quoteValidUntil: new Date(validUntil).toISOString(),
            });
          }}
        >
          <label className="k-field">
            <span className="k-field__label">{t("iss.revalidate.offerId")}</span>
            <input
              className="k-input ui-input"
              data-testid="revalidate-offer-id"
              value={offerId}
              onChange={(e) => setOfferId(e.target.value)}
            />
            <span className="k-field__hint">{t("iss.revalidate.offerIdHint")}</span>
          </label>
          <DateTimeField
            label={t("iss.revalidate.acceptedAt")}
            value={acceptedAt}
            onChange={setAcceptedAt}
            todayLabel={t("df.today")}
            nowLabel={t("df.now")}
            data-testid="revalidate-accepted-at"
          />
          <DateTimeField
            label={t("iss.revalidate.validUntil")}
            value={validUntil}
            onChange={setValidUntil}
            todayLabel={t("df.today")}
            nowLabel={t("df.now")}
            data-testid="revalidate-valid-until"
          />
          <button
            type="submit"
            className="k-btn k-btn--primary"
            data-testid="action-revalidate"
            disabled={!ready || revalidate.isPending}
          >
            <Icon name="arrow-right" size={16} />
            {t("iss.action.revalidate")}
          </button>
          {/* QA round — the whole surface rendered mutation errors NOWHERE: a 409
              (a concurrent terminate, a stale tab) or a 400 left the button
              re-enabled and the screen unchanged. The household surface already has
              this idiom; issuance did not. */}
          {revalidate.error instanceof ProblemError && (
            <p role="alert" className="k-field__error" data-testid="revalidate-error">
              {revalidate.error.problem.detail}
            </p>
          )}
        </form>
      ) : (
        <p role="note" className="muted small">
          {t("iss.needWrite")}
        </p>
      )}
    </div>
  );
}

// D16 — the two terminal exits. Both endpoints shipped and neither had a control, so
// an application that would never issue could not be retired and sat in the ops queue
// indefinitely. They are deliberately NOT one button with a dropdown: withdraw means
// WE stopped, record-declined means the CARRIER refused, and the carrier-quality loop
// reads that difference. Both require a reason (server: 1..500) and both 409 on an
// already-terminal application, which is why the caller only renders this for
// non-terminal statuses.
function TerminateStage({ id, canWrite, t }: { id: string; canWrite: boolean; t: TFn }) {
  const withdraw = useWithdrawApplication(id);
  const declined = useRecordDeclined(id);
  const [reason, setReason] = useState("");
  const busy = withdraw.isPending || declined.isPending;
  const ready = reason.trim().length > 0;
  // Whichever of the two failed — both 409 on an already-terminal application.
  const terminateError =
    withdraw.error instanceof ProblemError
      ? withdraw.error
      : declined.error instanceof ProblemError
        ? declined.error
        : null;

  if (!canWrite) return null;

  return (
    <div className="issuance-stage k-card" data-testid="stage-terminate">
      <p className="surface__section-label" style={{ marginTop: 0 }}>
        {t("iss.stage.terminate")}
      </p>
      <p className="muted small" data-testid="terminate-hint">
        {t("iss.stage.terminateHint")}
      </p>
      <div className="issuance-form">
        <label className="k-field">
          <span className="k-field__label">{t("iss.terminate.reason")}</span>
          <input
            className="k-input ui-input"
            data-testid="terminate-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </label>
        <div className="issuance-actions">
          <button
            type="button"
            className="k-btn k-btn--secondary"
            data-testid="action-withdraw"
            disabled={!ready || busy}
            onClick={() => withdraw.mutate({ reason: reason.trim() })}
          >
            {t("iss.action.withdraw")}
          </button>
          <button
            type="button"
            className="k-btn k-btn--secondary"
            data-testid="action-record-declined"
            disabled={!ready || busy}
            onClick={() => declined.mutate({ reason: reason.trim() })}
          >
            {t("iss.action.recordDeclined")}
          </button>
        </div>
        {/* Both of these 409 on an already-terminal application — the single most
            likely conflict on this screen. Silence there would be the worst case. */}
        {terminateError && (
          <p role="alert" className="k-field__error" data-testid="terminate-error">
            {terminateError.problem.detail}
          </p>
        )}
      </div>
    </div>
  );
}
